import React, { Component, Fragment } from 'react';

import classNames from 'classnames';

import { normalizeImageUrl } from '../../../../../lib/utils/image';

import compose from '../../../../../lib/utils/compose';

import { biLogger } from '../../../../../client/bi';

import {
  withSettings,
  IWithSettingsProps,
} from '../../../../../components/context/settings';
import { withTPA, IWithTPAProps } from '../../../../../components/context/tpa';
import {
  withI18n,
  IWithI18nProps,
} from '../../../../../components/context/i18n';

import { TPADeviceType } from '@wix/podcasts-common/dist/tpa';

import {
  EpisodeLink,
  navigateToEpisodePage,
} from '../../../../../components/nav';
import DateTime from '../../../../../components/ui/DateTime';
import { ImgCover, ImgCoverStyle } from '../../../../../components/ui/img';

import { IPodcastEpisode } from '@wix/podcasts-common/dist/podcast';

import styles from './EpisodesListItem.scss';
import getStyles from './EpisodesListItem.styles';
import { mainEpisodeClick } from '@wix/bi-logger-podcast/v2';

interface IEpisodesListItemProps
  extends IWithSettingsProps,
    IWithTPAProps,
    IWithI18nProps {
  podcastImage: string;
  episode: IPodcastEpisode;
  isFirst: boolean;
  isLatest: boolean;
  shouldFocus: boolean;
}

class EpisodesListItem extends Component<IEpisodesListItemProps> {
  linkRef = React.createRef<HTMLAnchorElement>();

  componentDidMount(): void {
    if (this.props.shouldFocus) {
      this.linkRef.current?.focus?.();
    }
  }

  onEpisodeLinkClick = () => {
    biLogger.report(
      mainEpisodeClick({
        episodeId: this.props.episode.id,
      }),
    );
  };

  redirectToEpisodeOnClick = () => {
    this.onEpisodeLinkClick();
    navigateToEpisodePage(this.props.episode.id, this.props.episode.slug);
  };
  renderImageContainer(dynamicStyle) {
    const { podcastImage, episode, settings } = this.props;

    const mainSettings = settings.main;

    return (
      mainSettings.showEpisodeImage && (
        <div
          className={classNames(
            styles.imageContainer,
            dynamicStyle.imageContainer,
          )}
        >
          <ImgCover
            data-hook="main.episodes.episode.image"
            coverStyle={ImgCoverStyle.SQUARE}
            src={normalizeImageUrl(episode.image || podcastImage)}
            title={episode.title}
          />
          <div className={styles.imageOverlay} />
        </div>
      )
    );
  }

  renderLatestTag(dynamicStyle) {
    const { t } = this.props;

    return (
      <span
        data-hook="main.episodes.episode.latest-label"
        className={classNames(styles.episodeLabel, dynamicStyle.episodeLabel)}
      >
        {t('pages.main.episode-latest-label')}
      </span>
    );
  }

  renderTitleContainer(dynamicStyle) {
    const { episode } = this.props;

    return (
      <EpisodeLink
        id={episode.id}
        slug={episode.slug}
        onClick={this.onEpisodeLinkClick}
        className={styles.linkContainer}
        dataHook="main.episodes.episode.link"
        linkRef={this.linkRef}
      >
        <div className={styles.episodeTitleContainer}>
          <h2
            data-hook="main.episodes.episode.title"
            className={classNames(
              styles.episodeTitle,
              dynamicStyle.episodeTitle,
            )}
          >
            {episode.title}
          </h2>
        </div>
      </EpisodeLink>
    );
  }

  renderMetaInfo(dynamicStyle) {
    const { episode, settings, tpa, isLatest } = this.props;

    const { showEpisodeDate, showEpisodeDuration } = settings.main;

    const showLatestEpisodeText =
      settings.main.showLatestEpisodeText &&
      isLatest &&
      tpa.params.deviceType !== TPADeviceType.MOBILE;

    return (
      (showEpisodeDate || showEpisodeDuration || showLatestEpisodeText) && (
        <div
          className={classNames(
            styles.episodeMetaInfo,
            dynamicStyle.episodeMetaInfo,
          )}
        >
          {showEpisodeDate && (
            <DateTime
              type="date"
              value={episode.date}
              dataHook="main.episodes.episode.date"
              className={styles.episodeDate}
            />
          )}
          {showEpisodeDate && showEpisodeDuration && (
            <span
              aria-hidden="true"
              role="presentation"
              className={styles.episodeDotDivider}
            >
              |
            </span>
          )}
          {showEpisodeDuration && (
            <DateTime
              type="duration"
              value={episode.duration}
              dataHook="main.episodes.episode.duration"
              className={styles.episodeDuration}
            />
          )}
          {showLatestEpisodeText && this.renderLatestTag(dynamicStyle)}
        </div>
      )
    );
  }

  renderDescription(dynamicStyle) {
    const { episode, settings } = this.props;

    const mainSettings = settings.main;

    return (
      mainSettings.showEpisodeDescription && (
        <div
          data-hook="main.episodes.episode.description"
          className={classNames(
            styles.episodeDescription,
            dynamicStyle.episodeDescription,
          )}
        >
          {episode.description}
        </div>
      )
    );
  }

  renderEpisodeMobile(dynamicStyle) {
    return (
      <Fragment>
        <div className={styles.topPartContainer}>
          {this.renderImageContainer(dynamicStyle)}
          <div className={styles.infoContainer}>
            <div className={styles.textInfoContainer}>
              {this.renderTitleContainer(dynamicStyle)}
              {this.renderMetaInfo(dynamicStyle)}
            </div>
            <div
              className={classNames(styles.rightArr, dynamicStyle.rightArr)}
            />
          </div>
        </div>
        {this.renderDescription(dynamicStyle)}
      </Fragment>
    );
  }

  renderEpisodeDesktop(dynamicStyle) {
    return (
      <>
        {this.renderImageContainer(dynamicStyle)}
        <div className={styles.infoContainer}>
          {this.renderTitleContainer(dynamicStyle)}
          {this.renderMetaInfo(dynamicStyle)}
          {this.renderDescription(dynamicStyle)}
        </div>
      </>
    );
  }

  render() {
    const { isFirst, tpa, settings } = this.props;

    const deviceType = tpa.params.deviceType;
    const mainSettings = settings.main;

    const dynamicStyle = getStyles({
      colors: settings.colors.main,
      fonts: settings.fonts.main,
      settings: mainSettings,
      deviceType,
    });

    return (
      <li data-hook="main.episodes.episode" role="group">
        {!isFirst && (
          <div
            className={classNames(
              styles.episodeDivider,
              dynamicStyle.episodeDivider,
            )}
          />
        )}
        <div
          className={classNames(styles.episode, dynamicStyle.episode)}
          onClick={this.redirectToEpisodeOnClick}
        >
          {deviceType === TPADeviceType.MOBILE
            ? this.renderEpisodeMobile(dynamicStyle)
            : this.renderEpisodeDesktop(dynamicStyle)}
        </div>
      </li>
    );
  }
}

export default compose(withI18n, withTPA, withSettings)(EpisodesListItem);
